// BrimLight/client/helpers/video-editor/ClipEditorFormConfig.js

import { MediaSelector } from "../../ui/MediaSelector.js";
import { KenBurnsAnimator } from "./KenBurnsAnimator.js";
import ClipSchema from "../../../schemas/collections/clips.js";

export function getClipEditorFormConfig() {
    const imageEffectOptions = KenBurnsAnimator.getAvailableEffects().map(effectName => ({
        value: effectName,
        label: KenBurnsAnimator.getEffectDisplayName(effectName)
    }));

    return {
        schema: ClipSchema,
        fields: {
            type: { 
                type: 'select', 
                label: 'Clip Type', 
                options: ['visual', 'dialogue', 'sfx', 'bgMusic', 'ambient', 'layer'], 
                required: true 
            },
            startTime: { type: 'number', label: 'Start Time (s)', required: true, step: 0.1 },
            duration: { type: 'number', label: 'Duration (s)', required: true, step: 0.1 },
            
            // Visual Clip Fields
            prompt: { type: 'textarea', label: 'Prompt', rows: 3, visibleWhen: (formData) => ['sfx', 'bgMusic', 'ambient', 'visual'].includes(formData.type) },
            assetPath: {
                type: 'MediaSelector', label: 'Source Media File', component: MediaSelector, category: 'image', maxFiles: 1,
                visibleWhen: (formData) => ['visual', 'sfx', 'bgMusic', 'ambient', 'dialogue'].includes(formData.type),
                onParentFormUpdate: (formData, componentInstance) => {
                    const newCategory = ['sfx', 'bgMusic', 'ambient', 'dialogue'].includes(formData.type) ? 'audio' : 'image';
                    if (componentInstance.config.category !== newCategory) {
                        componentInstance.setConfig({ category: newCategory });
                        componentInstance.render();
                    }
                }
            },
            videoGenerationPrompt: { type: 'textarea', label: 'Video Generation Prompt', rows: 2, visibleWhen: (formData) => formData.type === 'visual' },
            imageEffect: { type: 'select', label: 'Image Animation Effect', options: imageEffectOptions, visibleWhen: (formData) => formData.type === 'visual' },
            transitionEffect: {
                type: 'select', label: 'Transition Effect',
                options: ['cut', 'crossfade', 'dip_to_black', 'dip_to_white', 'wipe_left', 'wipe_right', 'wipe_up', 'wipe_down', 'slide_left', 'slide_right', 'slide_up', 'slide_down', 'zoom_in', 'zoom_out'],
                visibleWhen: (formData) => formData.type === 'visual'
            },

            // Dialogue Clip Fields
            dialogue: { type: 'textarea', label: 'Dialogue Text', rows: 3, visibleWhen: (formData) => formData.type === 'dialogue' },

            // Layer (Text) Clip Fields
            text: { type: 'textarea', label: 'Text Content', rows: 3, visibleWhen: (formData) => formData.type === 'layer' },
            'position.preset': { type: 'select', label: 'Position', options: ['top-left', 'top-center', 'top-right', 'center-left', 'center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'], visibleWhen: (formData) => formData.type === 'layer' },
            'animation.inEffect': { type: 'select', label: 'Entrance Effect', options: ['fade', 'slide-up', 'typewriter', 'pop', 'stomp', 'burst'], visibleWhen: (formData) => formData.type === 'layer' },
            // --- START OF FIX: Hide inDuration for typewriter ---
            'animation.inDuration': { type: 'number', label: 'Entrance Duration (s)', step: 0.1, visibleWhen: (formData) => formData.type === 'layer' && formData.animation?.inEffect !== 'typewriter' },
            // --- END OF FIX ---
            'animation.outEffect': { type: 'select', label: 'Exit Effect', options: ['fade', 'slide-down', 'zoom-out', 'pop-out'], visibleWhen: (formData) => formData.type === 'layer' },
            'animation.outDuration': { type: 'number', label: 'Exit Duration (s)', step: 0.1, visibleWhen: (formData) => formData.type === 'layer' },
            'styling.fontSize': { type: 'select', label: 'Font Size', options: ['small', 'medium', 'large', 'xlarge'], visibleWhen: (formData) => formData.type === 'layer' },
            'styling.color': { type: 'color', label: 'Text Color', visibleWhen: (formData) => formData.type === 'layer' },
            'styling.backgroundColor': { type: 'text', label: 'Background Color', helpText: "e.g., rgba(0,0,0,0.5)", visibleWhen: (formData) => formData.type === 'layer' },

            // Common Asset Association Fields
            characterIds: { type: 'TagInput', label: 'Associated Characters', component: 'TagInput', optionsSourceRpc: 'CharacterRpc.getFieldValues', visibleWhen: (formData) => ['visual', 'dialogue'].includes(formData.type) },
            environmentId: { type: 'select', label: 'Associated Environment', optionsSourceRpc: { method: 'EnvironmentRpc.getFieldValues' }, visibleWhen: (formData) => formData.type === 'visual' },
            propIds: { type: 'TagInput', label: 'Associated Props', component: 'TagInput', optionsSourceRpc: 'PropRpc.getFieldValues', visibleWhen: (formData) => formData.type === 'visual' }
        }
    };
}